<script setup>
import { h, computed, onMounted } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - feature-permission gate for AI Studio
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
// NOTICE: KAPTION-INTEGRATION - security-center.UI.2
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
// NOTICE: KAPTION-INTEGRATION - team-location.HIDDEN.1
import { useKaptionTeamLocationAvailability } from 'dashboard/composables/useKaptionTeamLocationAvailability';
import { useFullScreenDashboardApps } from 'dashboard/composables/useFullScreenDashboardApps';
// NOTICE: KAPTION-INTEGRATION - home-dashboard-app.WEB.7
import { useKaptionHomeApp } from 'dashboard/composables/useKaptionHomeApp';

import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
// NOTICE: KAPTION-INTEGRATION - rumo-app-updates.MARK.1
import KaptionUpdateMark from './KaptionUpdateMark.vue';
import KaptionNewMessageButton from 'dashboard/components-next/NewConversationModal/KaptionNewMessageButton.vue';
import KaptionSidebarScrollContainer from './KaptionSidebarScrollContainer.vue';
// NOTICE: KAPTION-INTEGRATION - Favorites
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';
import { buildStarredChildren } from './kaptionStarredItemsHelper';
// NOTICE: KAPTION-INTEGRATION - Sidebar filter/section visibility gates
import { applySidebarFilterGates } from './kaptionSidebarFilterGates';
// NOTICE: KAPTION-INTEGRATION - Asistentes replaces the AI Studio entry (KAPTION_AGENT_PROVIDERS)
import { buildKaptionAssistantsSidebarItem } from './kaptionAssistantsSidebar';
import { useAdmin } from 'dashboard/composables/useAdmin';

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'toggleSidebarVersion',
]);

const { accountScopedRoute } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();
const { isFeatureFlagEnabled } = usePolicy();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
// NOTICE: KAPTION-INTEGRATION - Asistentes pages are administrator-only
const { isAdmin } = useAdmin();
// NOTICE: KAPTION-INTEGRATION - security-center.UI.2 (flag + permission)
const { isEnabled: isKaptionSecurityEnabled } = useKaptionSecurity();
// NOTICE: KAPTION-INTEGRATION - feature-permission gate for AI Studio
const { canUseFeature } = useFeaturePermissions();
// NOTICE: KAPTION-INTEGRATION - team-location.HIDDEN.1 (fail-closed, unlike canUseFeature)
const { isTeamLocationEnabled, canViewTeamLocation } =
  useKaptionTeamLocationAvailability();
const getters = useStoreGetters();
// NOTICE: KAPTION-INTEGRATION - Favorites
const { mergedFavorites } = useKaptionFavorites();

const enabledMapFeatures = {
  inbox: true,
  conversations: true,
  captain: false,
  contacts: true,
  reports: true,
  campaigns: true,
  portals: true,
  settings: true,
};

const enabledReportFeatures = {
  overview: true,
  conversations: true,
  agents: true,
  labels: true,
  inbox: true,
  teams: true,
  csat: false,
  sla: false,
  bot: false,
};

const enabledSettingsFeatures = {
  accountSettings: true,
  agents: true,
  teams: true,
  inboxes: true,
  labels: true,
  customAttributes: true,
  automation: isFeatureFlagEnabled('kaption_automations'),
  agentBots: isFeatureFlagEnabled('kaption_bots'),
  macros: true,
  cannedResponses: isFeatureFlagEnabled('kaption_canned_responses'),
  integrations: true,
  auditLogs: false,
  customRoles: false,
  sla: false,
  billing: true,
  // NOTICE: KAPTION-INTEGRATION - Kaption Access Tokens
  kaptionAccessTokens: isKaptionFlagEnabled('KAPTION_ACCESS_TOKENS'),
  // NOTICE: KAPTION-INTEGRATION - Kaption Delivery Webhooks
  kaptionDeliveryWebhooks: isKaptionFlagEnabled('KAPTION_DELIVERY_WEBHOOKS'),
  // NOTICE: KAPTION-INTEGRATION - Kaption Response Policies
  kaptionResponsePolicies: isKaptionFlagEnabled('KAPTION_RESPONSE_POLICIES'),
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
// NOTICE: KAPTION-INTEGRATION
const assistants = useMapGetter('kaptionAssistants/getAssistants');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
// NOTICE: KAPTION-INTEGRATION
const getAccount = useMapGetter('accounts/getAccount');
const getCurrentAccountId = useMapGetter('getCurrentAccountId');
const currentAccount = computed(() =>
  getAccount.value(getCurrentAccountId.value)
);
const hasPlaygroundUrl = computed(
  () => !!currentAccount.value?.settings?.playground_url
);
// NOTICE: KAPTION-INTEGRATION - Account setting to default to All Conversations
const defaultAllConversations = computed(
  () => !!currentAccount.value?.settings?.default_all_conversations
);

// NOTICE: KAPTION-INTEGRATION
const hasAiStudioUrl = computed(() => {
  const accountStudioUrl = currentAccount.value?.settings?.ai_studio_url;
  const globalStudioUrl = getters['globalConfig/get'].value.aiStudioUrl;
  return !!(accountStudioUrl || globalStudioUrl);
});

// NOTICE: KAPTION-INTEGRATION
// AI Studio visibility logic:
// - kaption_ai_studio (Chatwoot feature flag) + ai_studio_url → Show AI Studio
// - KAPTION_AI_STUDIO_SIDEBAR (Kaption feature flag) → Use new dropdown navigation
// - If KAPTION_AI_STUDIO_SIDEBAR is disabled → Use simple link (old behavior)
const aiStudioVisibility = computed(() => {
  const hasUrl = hasAiStudioUrl.value;
  const baseFeatureEnabled = isFeatureFlagEnabled('kaption_ai_studio');
  const sidebarFeatureEnabled = isKaptionFlagEnabled(
    'KAPTION_AI_STUDIO_SIDEBAR'
  );

  return {
    hasUrl,
    baseFeatureEnabled,
    sidebarFeatureEnabled,
    // NOTICE: KAPTION-INTEGRATION - gate AI Studio behind the feature-permission scope
    shouldShow: hasUrl && baseFeatureEnabled && canUseFeature('ai_studio'),
    useNewSidebar: sidebarFeatureEnabled,
  };
});

// NOTICE: KAPTION-INTEGRATION - Full-screen dashboard apps in sidebar
const { fullScreenApps, ensureLoaded: ensureFullScreenAppsLoaded } =
  useFullScreenDashboardApps();
// NOTICE: KAPTION-INTEGRATION - home-dashboard-app.WEB.7
const { homeAppId } = useKaptionHomeApp();

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  // NOTICE: KAPTION-INTEGRATION
  store.dispatch('kaptionAssistants/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  // NOTICE: KAPTION-INTEGRATION - Load dashboard apps for full-screen sidebar icons
  ensureFullScreenAppsLoaded();
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

// NOTICE: KAPTION-INTEGRATION
const getKaptionAssistantItems = () => {
  if (assistants.value.length > 1) {
    return [
      {
        name: 'KaptionAssistants',
        label: t('SIDEBAR.KAPTION_ASSISTANTS_CONVERSATIONS'),
        icon: 'i-lucide-bot',
        activeOn: [
          'conversation_through_kaption_assistants',
          'conversation_kaption_assistant',
        ],
        children: assistants.value.map(assistant => ({
          name: `Assistant-${assistant.id}`,
          label: assistant.name,
          to: accountScopedRoute('conversation_kaption_assistant', {
            assistantId: assistant.id,
          }),
          // NOTICE: KAPTION-INTEGRATION - Star props
          starType: 'assistant',
          starId: assistant.id,
        })),
      },
    ];
  }

  if (assistants.value.length === 1) {
    return [
      {
        name: 'KaptionAssistants',
        activeOn: ['conversation_through_kaption_assistants'],
        label: assistants.value[0].name,
        to: accountScopedRoute('conversation_kaption_assistants'),
        // NOTICE: KAPTION-INTEGRATION - Star props
        starType: 'assistant',
        starId: assistants.value[0].id,
      },
    ];
  }

  return [];
};

// NOTICE: KAPTION-INTEGRATION - Favorites section
const getStarredSection = () => {
  if (!isKaptionFlagEnabled('KAPTION_SIDEBAR_STARRING')) return [];
  if (mergedFavorites.value.length === 0) return [];

  const children = buildStarredChildren(mergedFavorites.value, {
    inboxes: inboxes.value,
    labels: labels.value,
    teams: teams.value,
    assistants: assistants.value,
    folders: conversationCustomViews.value,
    accountScopedRoute,
    h,
    ChannelLeaf,
  });

  // Don't show section if no valid items exist in current account
  if (children.length === 0) return [];

  return [
    {
      name: 'Favorites',
      label: t('SIDEBAR.FAVORITES'),
      icon: 'i-lucide-star',
      activeOn: [
        'inbox_dashboard',
        'label_conversations',
        'team_conversations',
        'conversation_kaption_assistant',
        'folder_conversations',
      ],
      children,
    },
  ];
};

const menuItems = computed(() => {
  const items = [];

  // NOTICE: KAPTION-INTEGRATION - home-dashboard-app.WEB.7: the user's home
  // app is pinned first as "Home"; Conversations stays below.
  if (homeAppId.value) {
    items.push({
      name: 'KaptionHome',
      label: t('KAPTION_HOME_SCREEN.SIDEBAR_HOME'),
      icon: 'i-lucide-house',
      to: accountScopedRoute('fullscreen_dashboard_app', {
        appId: homeAppId.value,
      }),
    });
  }

  // Inbox - NOTICE: KAPTION-INTEGRATION - Hide when default_all_conversations account setting is enabled
  // NOTICE: KAPTION-INTEGRATION - ...and, only for accounts on the view
  // switcher (KAPTION_VIEW_SWITCHER), unless the sidebar_inbox feature
  // permission allows it (default 'off': hidden unless an admin turns it on).
  if (
    enabledMapFeatures.inbox &&
    !defaultAllConversations.value &&
    (!isKaptionFlagEnabled('KAPTION_VIEW_SWITCHER') ||
      canUseFeature('sidebar_inbox'))
  ) {
    items.push({
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        badge: 'notifications/getHasUnreadNotifications',
      },
    });
  }

  // NOTICE: KAPTION-INTEGRATION - Full-screen dashboard apps (between Inbox and Conversations)
  // (the home app is already pinned as "Home" above)
  fullScreenApps.value
    .filter(app => app.id !== homeAppId.value)
    .forEach(app => {
      const iconClass =
        app.icon?.type === 'icon' && app.icon?.value
          ? app.icon.value
          : 'i-lucide-layout-dashboard';
      items.push({
        name: `DashboardApp-${app.id}`,
        label: app.title,
        icon: iconClass,
        to: accountScopedRoute('fullscreen_dashboard_app', { appId: app.id }),
        activeOn: ['fullscreen_dashboard_app'],
      });
    });

  // Conversations
  if (enabledMapFeatures.conversations) {
    items.push({
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      // NOTICE: KAPTION-INTEGRATION - Entries/sections below are filtered by
      // the account's feature-permission scopes (kaptionSidebarFilterGates).
      children: applySidebarFilterGates(
        [
          // NOTICE: KAPTION-INTEGRATION - Order matches topbar filter cycle
          {
            name: 'All',
            label: t('SIDEBAR.ALL_CONVERSATIONS'),
            tooltip: t('SIDEBAR.FILTER_TOOLTIPS.ALL'),
            activeOn: ['inbox_conversation'],
            to: accountScopedRoute('home'),
          },
          {
            name: 'Unattended',
            activeOn: ['conversation_through_unattended'],
            label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
            tooltip: t('SIDEBAR.FILTER_TOOLTIPS.UNATTENDED'),
            to: accountScopedRoute('conversation_unattended'),
          },
          // NOTICE: KAPTION-INTEGRATION - Hide "Mine" when default_all_conversations account setting is enabled
          ...(defaultAllConversations.value
            ? []
            : [
                {
                  name: 'Mine',
                  label: t('SIDEBAR.MY_CONVERSATIONS'),
                  tooltip: t('SIDEBAR.FILTER_TOOLTIPS.MINE'),
                  activeOn: ['conversation_through_mine'],
                  to: {
                    ...accountScopedRoute('home'),
                    query: { assignee: 'me' },
                  },
                },
              ]),
          {
            name: 'Unassigned',
            label: t('SIDEBAR.UNASSIGNED_CONVERSATIONS'),
            tooltip: t('SIDEBAR.FILTER_TOOLTIPS.UNASSIGNED'),
            activeOn: ['conversation_through_unassigned'],
            to: {
              ...accountScopedRoute('home'),
              query: { assignee: 'unassigned' },
            },
          },
          // NOTICE: KAPTION-INTEGRATION - Unread filter sidebar entry
          // Routes to home with ?assignee=unread, which triggers
          // conversation_type=unread on the backend. No icon: its siblings
          // (Unattended/Mine/Unassigned/Mentions) render plain labels.
          {
            name: 'Unread',
            label: t('SIDEBAR.UNREAD_CONVERSATIONS'),
            tooltip: t('SIDEBAR.FILTER_TOOLTIPS.UNREAD'),
            activeOn: ['conversation_through_unread'],
            to: {
              ...accountScopedRoute('home'),
              query: { assignee: 'unread' },
            },
          },
          {
            name: 'Mentions',
            label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
            tooltip: t('SIDEBAR.FILTER_TOOLTIPS.MENTIONS'),
            activeOn: ['conversation_through_mentions'],
            to: accountScopedRoute('conversation_mentions'),
          },
          // NOTICE: KAPTION-INTEGRATION - Starred items section
          ...getStarredSection(),
          // NOTICE: KAPTION-INTEGRATION
          ...getKaptionAssistantItems(),
          {
            name: 'Folders',
            label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
            icon: 'i-lucide-folder',
            activeOn: ['conversations_through_folders'],
            children: conversationCustomViews.value.map(view => ({
              name: `${view.name}-${view.id}`,
              label: view.name,
              to: accountScopedRoute('folder_conversations', { id: view.id }),
              // NOTICE: KAPTION-INTEGRATION - Star props
              starType: 'folder',
              starId: view.id,
            })),
          },
          {
            name: 'Teams',
            label: t('SIDEBAR.TEAMS'),
            icon: 'i-lucide-users',
            activeOn: ['conversations_through_team'],
            children: teams.value.map(team => ({
              name: `${team.name}-${team.id}`,
              label: team.name,
              to: accountScopedRoute('team_conversations', { teamId: team.id }),
              // NOTICE: KAPTION-INTEGRATION - Star props
              starType: 'team',
              starId: team.id,
            })),
          },
          {
            name: 'Channels',
            label: t('SIDEBAR.CHANNELS'),
            icon: 'i-lucide-mailbox',
            activeOn: ['conversation_through_inbox'],
            children: sortedInboxes.value.map(inbox => ({
              name: `${inbox.name}-${inbox.id}`,
              label: inbox.name,
              to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
              component: leafProps =>
                h(ChannelLeaf, {
                  label: leafProps.label,
                  active: leafProps.active,
                  inbox,
                }),
              // NOTICE: KAPTION-INTEGRATION - Star props
              starType: 'channel',
              starId: inbox.id,
            })),
          },
          {
            name: 'Labels',
            label: t('SIDEBAR.LABELS'),
            icon: 'i-lucide-tag',
            activeOn: ['conversations_through_label'],
            children: labels.value.map(label => ({
              name: `${label.title}-${label.id}`,
              label: label.title,
              icon: h('span', {
                class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
                style: { backgroundColor: label.color },
              }),
              to: accountScopedRoute('label_conversations', {
                label: label.title,
              }),
              // NOTICE: KAPTION-INTEGRATION - Star props
              starType: 'label',
              starId: label.id,
            })),
          },
        ],
        canUseFeature,
        { inboxCount: sortedInboxes.value.length }
      ),
    });
  }

  // Captain
  if (enabledMapFeatures.captain) {
    // Removed Captain section as requested
  }

  // NOTICE: KAPTION-INTEGRATION
  // AI Studio - show if ai_studio_url is configured and kaption_ai_studio feature flag enabled
  // If KAPTION_AI_STUDIO_SIDEBAR is enabled: Show new dropdown with hierarchy
  // If KAPTION_AI_STUDIO_SIDEBAR is disabled: Show simple link (old behavior)
  // NOTICE: KAPTION-INTEGRATION - Asistentes: with KAPTION_AGENT_PROVIDERS on (and an
  // administrator), ONE flat "Asistentes" group replaces the AI Studio entry below.
  // Flag off: the AI Studio entry renders exactly as before.
  const kaptionAssistantsItem = buildKaptionAssistantsSidebarItem({
    isKaptionFlagEnabled,
    isAdmin: isAdmin.value,
    showStudioChat: aiStudioVisibility.value.shouldShow,
    t,
    accountScopedRoute,
  });
  if (kaptionAssistantsItem) {
    items.push(kaptionAssistantsItem);
  } else if (aiStudioVisibility.value.shouldShow) {
    if (aiStudioVisibility.value.useNewSidebar) {
      // New sidebar with dropdown navigation
      items.push({
        name: 'AI Studio',
        label: t('SIDEBAR.AI_STUDIO'),
        icon: 'i-lucide-brain',
        children: [
          {
            name: 'AI Studio Home',
            label: t('AI_STUDIO.SIDEBAR.HOME'),
            to: accountScopedRoute('ai_studio_index', {}, { section: 'home' }),
          },
          {
            name: 'Knowledge Base',
            label: t('AI_STUDIO.SIDEBAR.KNOWLEDGE_BASE'),
            icon: 'i-lucide-database',
            children: [
              {
                name: 'Knowledge Chat',
                label: t('AI_STUDIO.SIDEBAR.KNOWLEDGE_CHAT'),
                to: accountScopedRoute(
                  'ai_studio_index',
                  {},
                  { section: 'knowledge-chat' }
                ),
              },
              {
                name: 'Knowledge Search',
                label: t('AI_STUDIO.SIDEBAR.KNOWLEDGE_SEARCH'),
                to: accountScopedRoute(
                  'ai_studio_index',
                  {},
                  { section: 'knowledge-search' }
                ),
              },
              {
                name: 'Knowledge Sources',
                label: t('AI_STUDIO.SIDEBAR.KNOWLEDGE_SOURCES'),
                to: accountScopedRoute(
                  'ai_studio_index',
                  {},
                  { section: 'knowledge-sources' }
                ),
              },
            ],
          },
          {
            name: 'Assistants Group',
            label: t('AI_STUDIO.SIDEBAR.ASSISTANTS'),
            icon: 'i-lucide-bot',
            children: [
              {
                name: 'AI Studio Assistants',
                label: t('AI_STUDIO.SIDEBAR.ASSISTANTS'),
                to: accountScopedRoute(
                  'ai_studio_index',
                  {},
                  { section: 'assistants' }
                ),
              },
              {
                name: 'AI Studio Tools',
                label: t('AI_STUDIO.SIDEBAR.TOOLS'),
                to: accountScopedRoute(
                  'ai_studio_index',
                  {},
                  { section: 'tools' }
                ),
              },
            ],
          },
          {
            name: 'Activity',
            label: t('AI_STUDIO.SIDEBAR.ACTIVITY'),
            icon: 'i-lucide-clock',
            children: [
              {
                name: 'Activity Dashboard',
                label: t('AI_STUDIO.SIDEBAR.ACTIVITY_DASHBOARD'),
                to: accountScopedRoute(
                  'ai_studio_index',
                  {},
                  { section: 'activity-dashboard' }
                ),
              },
              {
                name: 'Activity Chat Logs',
                label: t('AI_STUDIO.SIDEBAR.ACTIVITY_CHAT_LOGS'),
                to: accountScopedRoute(
                  'ai_studio_index',
                  {},
                  { section: 'activity-chat-logs' }
                ),
              },
            ],
          },
          {
            name: 'AI Studio Settings',
            label: t('AI_STUDIO.SIDEBAR.SETTINGS'),
            to: accountScopedRoute(
              'ai_studio_index',
              {},
              { section: 'settings' }
            ),
          },
        ],
      });
    } else {
      // Old behavior - simple link without dropdown
      items.push({
        name: 'AI Studio',
        label: t('SIDEBAR.AI_STUDIO'),
        icon: 'i-lucide-brain',
        to: accountScopedRoute('ai_studio_index'),
        activeOn: ['ai_studio_index'],
      });
    }
  }

  // NOTICE: KAPTION-INTEGRATION - app-studio.UI.1: App Studio (Builder-hosted),
  // behind the `app_studio` feature permission and KAPTION_APP_STUDIO.
  if (
    isKaptionFlagEnabled('KAPTION_APP_STUDIO') &&
    canUseFeature('app_studio')
  ) {
    items.push({
      name: 'App Studio',
      label: t('KAPTION_APP_STUDIO.SIDEBAR'),
      icon: 'i-lucide-blocks',
      to: accountScopedRoute('kaption_app_studio_index'),
      activeOn: [
        'kaption_app_studio_index',
        'kaption_app_studio_app',
        'kaption_app_studio_app_tab',
      ],
    });
  }

  // Contacts
  if (enabledMapFeatures.contacts) {
    items.push({
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    });
  }

  // Reports
  if (enabledMapFeatures.reports) {
    const reportChildren = [];

    if (enabledReportFeatures.overview) {
      reportChildren.push({
        name: 'Report Overview',
        label: t('SIDEBAR.REPORTS_OVERVIEW'),
        to: accountScopedRoute('account_overview_reports'),
      });
    }

    if (enabledReportFeatures.conversations) {
      reportChildren.push({
        name: 'Report Conversation',
        label: t('SIDEBAR.REPORTS_CONVERSATION'),
        to: accountScopedRoute('conversation_reports'),
      });
    }

    // Add filtered report routes based on enabled features
    const filteredReportRoutes = reportRoutes.value.filter(route => {
      if (route.name.includes('Agent') && !enabledReportFeatures.agents)
        return false;
      if (route.name.includes('Label') && !enabledReportFeatures.labels)
        return false;
      if (route.name.includes('Inbox') && !enabledReportFeatures.inbox)
        return false;
      if (route.name.includes('Team') && !enabledReportFeatures.teams)
        return false;
      return true;
    });
    reportChildren.push(...filteredReportRoutes);

    if (enabledReportFeatures.csat) {
      reportChildren.push({
        name: 'Reports CSAT',
        label: t('SIDEBAR.CSAT'),
        to: accountScopedRoute('csat_reports'),
      });
    }

    if (enabledReportFeatures.sla) {
      reportChildren.push({
        name: 'Reports SLA',
        label: t('SIDEBAR.REPORTS_SLA'),
        to: accountScopedRoute('sla_reports'),
      });
    }

    if (enabledReportFeatures.bot) {
      reportChildren.push({
        name: 'Reports Bot',
        label: t('SIDEBAR.REPORTS_BOT'),
        to: accountScopedRoute('bot_reports'),
      });
    }

    items.push({
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: reportChildren,
    });
  }

  // NOTICE: KAPTION-INTEGRATION - whatsapp-calling.WEB.3: Calls workspace
  // (All / Missed / Waiting permission), only while KAPTION_WHATSAPP_CALLING.
  if (isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING')) {
    items.push({
      name: 'Kaption Calls',
      label: t('KAPTION_CALLS.SIDEBAR.CALLS'),
      icon: 'i-lucide-phone',
      children: [
        {
          name: 'Kaption Calls All',
          label: t('KAPTION_CALLS.WORKSPACE.VIEWS.ALL'),
          to: accountScopedRoute('kaption_calls', { view: 'all' }),
        },
        {
          name: 'Kaption Calls Missed',
          label: t('KAPTION_CALLS.WORKSPACE.VIEWS.MISSED'),
          to: accountScopedRoute('kaption_calls', { view: 'missed' }),
        },
        {
          name: 'Kaption Calls Waiting',
          label: t('KAPTION_CALLS.WORKSPACE.VIEWS.WAITING'),
          to: accountScopedRoute('kaption_calls', { view: 'waiting' }),
        },
      ],
    });
  }

  // NOTICE: KAPTION-INTEGRATION - team-location.UI.1 / HIDDEN.1: live team map,
  // only while location is enabled for the account and the user is inside the
  // `team_location_view` scope. Fail-closed: hidden when the payload is absent.
  if (canViewTeamLocation.value) {
    items.push({
      name: 'Team Location',
      label: t('KAPTION_PLATFORM.TEAM_LOCATION.SIDEBAR'),
      icon: 'i-lucide-map-pin',
      to: accountScopedRoute('kaption_team_location_map'),
    });
  }

  // NOTICE: KAPTION-INTEGRATION - Outbound: Broadcast (bulk WhatsApp) + Direct (1-to-1 sends)
  if (enabledMapFeatures.campaigns) {
    items.push({
      name: 'Outbound',
      label: t('SIDEBAR.OUTBOUND'),
      icon: 'i-lucide-megaphone',
      children: [
        ...(canUseFeature('campaign_broadcasts')
          ? [
              {
                name: 'Broadcast',
                label: t('SIDEBAR.BROADCAST'),
                to: accountScopedRoute('campaigns_whatsapp_index'),
              },
            ]
          : []),
        ...(canUseFeature('whatsapp_direct_send')
          ? [
              {
                name: 'Direct',
                label: t('SIDEBAR.DIRECT'),
                to: accountScopedRoute('campaigns_direct_index'),
              },
            ]
          : []),
        // NOTICE: KAPTION-INTEGRATION - Aggregate Campaign Reports (feature-flagged)
        ...(isKaptionFlagEnabled('KAPTION_CAMPAIGN_REPORTS') &&
        canUseFeature('campaign_reports')
          ? [
              {
                name: 'Campaign Reports',
                label: t('SIDEBAR.CAMPAIGN_REPORTS'),
                to: accountScopedRoute('campaigns_reports_index'),
              },
            ]
          : []),
      ],
    });
  }

  // Portals
  if (enabledMapFeatures.portals) {
    items.push({
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    });
  }

  // NOTICE: KAPTION-INTEGRATION
  // Playground - only show if playground_url is configured
  if (hasPlaygroundUrl.value) {
    items.push({
      name: 'Playground',
      label: t('SIDEBAR.PLAYGROUND'),
      icon: 'i-lucide-flask-conical',
      to: accountScopedRoute('playground_index'),
      activeOn: ['playground_index'],
    });
  }

  // Settings
  if (enabledMapFeatures.settings) {
    const settingsChildren = [];

    if (enabledSettingsFeatures.accountSettings) {
      settingsChildren.push({
        name: 'Settings Account Settings',
        label: t('SIDEBAR.ACCOUNT_SETTINGS'),
        icon: 'i-lucide-briefcase',
        to: accountScopedRoute('general_settings_index'),
      });
    }

    if (enabledSettingsFeatures.agents) {
      settingsChildren.push({
        name: 'Settings Agents',
        label: t('SIDEBAR.AGENTS'),
        icon: 'i-lucide-square-user',
        to: accountScopedRoute('agent_list'),
      });
    }

    if (enabledSettingsFeatures.teams) {
      settingsChildren.push({
        name: 'Settings Teams',
        label: t('SIDEBAR.TEAMS'),
        icon: 'i-lucide-users',
        to: accountScopedRoute('settings_teams_list'),
      });
    }

    if (enabledSettingsFeatures.inboxes) {
      settingsChildren.push({
        name: 'Settings Inboxes',
        label: t('SIDEBAR.INBOXES'),
        icon: 'i-lucide-inbox',
        to: accountScopedRoute('settings_inbox_list'),
      });
    }

    if (enabledSettingsFeatures.labels) {
      settingsChildren.push({
        name: 'Settings Labels',
        label: t('SIDEBAR.LABELS'),
        icon: 'i-lucide-tags',
        to: accountScopedRoute('labels_list'),
      });
    }

    if (enabledSettingsFeatures.customAttributes) {
      settingsChildren.push({
        name: 'Settings Custom Attributes',
        label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
        icon: 'i-lucide-code',
        to: accountScopedRoute('attributes_list'),
      });
    }

    if (enabledSettingsFeatures.automation) {
      settingsChildren.push({
        name: 'Settings Automation',
        label: t('SIDEBAR.AUTOMATION'),
        icon: 'i-lucide-workflow',
        to: accountScopedRoute('automation_list'),
      });
    }

    if (enabledSettingsFeatures.agentBots) {
      settingsChildren.push({
        name: 'Settings Agent Bots',
        label: t('SIDEBAR.AGENT_BOTS'),
        icon: 'i-lucide-bot',
        to: accountScopedRoute('agent_bots'),
      });
    }

    if (enabledSettingsFeatures.macros) {
      settingsChildren.push({
        name: 'Settings Macros',
        label: t('SIDEBAR.MACROS'),
        icon: 'i-lucide-toy-brick',
        to: accountScopedRoute('macros_wrapper'),
      });
    }

    if (enabledSettingsFeatures.cannedResponses) {
      settingsChildren.push({
        name: 'Settings Canned Responses',
        label: t('SIDEBAR.CANNED_RESPONSES'),
        icon: 'i-lucide-message-square-quote',
        to: accountScopedRoute('canned_list'),
      });
    }

    if (enabledSettingsFeatures.integrations) {
      settingsChildren.push({
        name: 'Settings Integrations',
        label: t('SIDEBAR.INTEGRATIONS'),
        icon: 'i-lucide-blocks',
        to: accountScopedRoute('settings_applications'),
      });
    }

    if (enabledSettingsFeatures.auditLogs) {
      settingsChildren.push({
        name: 'Settings Audit Logs',
        label: t('SIDEBAR.AUDIT_LOGS'),
        icon: 'i-lucide-briefcase',
        to: accountScopedRoute('auditlogs_list'),
      });
    }

    if (enabledSettingsFeatures.customRoles) {
      settingsChildren.push({
        name: 'Settings Custom Roles',
        label: t('SIDEBAR.CUSTOM_ROLES'),
        icon: 'i-lucide-shield-plus',
        to: accountScopedRoute('custom_roles_list'),
      });
    }

    if (enabledSettingsFeatures.sla) {
      settingsChildren.push({
        name: 'Settings Sla',
        label: t('SIDEBAR.SLA'),
        icon: 'i-lucide-clock-alert',
        to: accountScopedRoute('sla_list'),
      });
    }

    if (enabledSettingsFeatures.billing) {
      settingsChildren.push({
        name: 'Settings Billing',
        label: t('SIDEBAR.BILLING'),
        icon: 'i-lucide-credit-card',
        to: accountScopedRoute('billing_settings_index'),
      });
    }

    // NOTICE: KAPTION-INTEGRATION - Kaption Access Tokens
    if (enabledSettingsFeatures.kaptionAccessTokens) {
      settingsChildren.push({
        name: 'Settings Kaption Access Tokens',
        label: t('SIDEBAR.KAPTION_ACCESS_TOKENS'),
        icon: 'i-lucide-key',
        to: accountScopedRoute('settings_kaption_access_tokens'),
      });
    }

    // NOTICE: KAPTION-INTEGRATION - Kaption Delivery Webhooks
    if (enabledSettingsFeatures.kaptionDeliveryWebhooks) {
      settingsChildren.push({
        name: 'Settings Kaption Delivery Webhooks',
        label: t('SIDEBAR.KAPTION_DELIVERY_WEBHOOKS'),
        icon: 'i-lucide-webhook',
        to: accountScopedRoute('settings_kaption_delivery_webhooks'),
      });
    }

    // NOTICE: KAPTION-INTEGRATION - team-location.SETTINGS.2 / HIDDEN.1 (route is
    // admin-only): only while location is enabled for the account.
    if (isTeamLocationEnabled.value) {
      settingsChildren.push({
        name: 'Settings Team Location',
        label: t('KAPTION_PLATFORM.TEAM_LOCATION.SIDEBAR'),
        icon: 'i-lucide-map-pin',
        to: accountScopedRoute('settings_kaption_team_location'),
      });
    }

    // NOTICE: KAPTION-INTEGRATION - security-center.UI.2: Security Center (admins,
    // KAPTION_SECURITY_CENTER flag + security_center permission)
    if (isAdmin.value && isKaptionSecurityEnabled.value) {
      settingsChildren.push({
        name: 'Settings Kaption Security',
        label: t('KAPTION_SECURITY.SIDEBAR'),
        icon: 'i-lucide-shield',
        to: accountScopedRoute('settings_kaption_security'),
      });
    }

    // NOTICE: KAPTION-INTEGRATION - Kaption Response Policies
    if (enabledSettingsFeatures.kaptionResponsePolicies) {
      settingsChildren.push({
        name: 'Settings Kaption Response Policies',
        label: t('SIDEBAR.KAPTION_RESPONSE_POLICIES'),
        icon: 'i-lucide-clock-alert',
        to: accountScopedRoute('settings_kaption_response_policies'),
      });
    }

    // NOTICE: KAPTION-INTEGRATION - Feature Management (route is admin-policed)
    settingsChildren.push({
      name: 'Settings Feature Management',
      label: t('SIDEBAR.FEATURE_MANAGEMENT'),
      icon: 'i-lucide-sliders-horizontal',
      to: accountScopedRoute('settings_feature_management'),
    });

    // NOTICE: KAPTION-INTEGRATION - home-dashboard-app.WEB.1 (route is admin-policed)
    settingsChildren.push({
      name: 'Settings Kaption Home Screen',
      label: t('KAPTION_HOME_SCREEN.SIDEBAR_SETTINGS'),
      icon: 'i-lucide-house',
      to: accountScopedRoute('settings_kaption_home_screen'),
    });

    // NOTICE: KAPTION-INTEGRATION
    // Kaption Debug - only show if feature flag enabled
    if (isKaptionFlagEnabled('KAPTION_FEATURE_FLAGS_DEBUG')) {
      settingsChildren.push({
        name: 'Settings Kaption Debug',
        label: t('SIDEBAR.KAPTION_DEBUG'),
        icon: 'i-lucide-bug',
        to: accountScopedRoute('kaption_debug_index'),
        activeOn: ['kaption_debug_index'],
      });
    }

    items.push({
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: settingsChildren,
    });
  }

  return items;
});
</script>

<template>
  <aside
    class="wp-card wp-sidebar w-[200px] bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak h-screen flex flex-col text-sm pb-1"
  >
    <section class="grid gap-2 mt-2 mb-4">
      <div class="flex items-center min-w-0 gap-2 px-2">
        <!-- NOTICE: KAPTION-INTEGRATION - rumo-app-updates.MARK.1-2: the logo
             button, which also says when Rumo needs a refresh or an update. -->
        <KaptionUpdateMark
          @toggle-sidebar-version="emit('toggleSidebarVersion')"
        />
        <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
        <SidebarAccountSwitcher
          class="flex-grow min-w-0 -mx-1"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
    </section>
    <KaptionSidebarScrollContainer>
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </KaptionSidebarScrollContainer>
    <section class="flex-shrink-0 flex flex-col gap-2">
      <div class="flex justify-end px-2">
        <button
          v-tooltip.right="t('SIDEBAR.COLLAPSE_SIDEBAR')"
          class="flex items-center justify-center size-9 rounded-lg hover:bg-n-alpha-2"
          @click="emit('toggleSidebarVersion')"
        >
          <span class="i-lucide-chevron-left size-9 text-n-slate-11" />
        </button>
      </div>
      <div class="flex gap-2 px-2">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-2 py-1 rounded-2xl h-7 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
          <span class="flex-grow text-left">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <KaptionNewMessageButton variant="row" />
      </div>
      <div
        class="w-full border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
      />
      <div class="p-1 flex justify-between gap-2 items-center">
        <SidebarProfileMenu
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
  </aside>
</template>

<style scoped>
/* NOTICE: KAPTION-INTEGRATION */
:deep(.kaption-compose-upward .w-\[42rem\]) {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 0.5rem !important;
  margin-top: 0 !important;
}
</style>
